/**
 * pane_light_node — the window light as a rect area light that knows its ROOM and its OCCLUDERS (shader gaps round 6,
 * agent J, 2026-09-30). The rules and their CPU mirrors are `lib/pane_visibility.ts`; this is the shader.
 *
 * `WindowAreaLight` is a `THREE.RectAreaLight` that carries per-light data (the room box, the people near its pane);
 * `WindowRectLightNode` extends three r185's own `RectAreaLightNode` and multiplies the light's colour — the ONE value
 * both LTC terms (diffuse and specular) and the shadowed light volume's rect term take — by:
 *   · the ROOM mask (`maskMode` 1: an interior pane lights only inside its building's box; 0: no mask), and
 *   · the OCCLUDER visibility (`visibility` true at creation): the ratio estimator over four stratified points of the
 *     rectangle past up to four vertical capsules (people), weighted by the unshadowed integrand — never one centre
 *     ray, never a spot light added on top. Skipped entirely (a uniform `If`) while no one stands near the pane.
 * The node is registered for the subclass (`registerWindowLightNode`) BEFORE any pooled light joins the scene: three
 * keys its light-node library by the light's CONSTRUCTOR, so an unregistered subclass would be skipped with a warning.
 *
 * FRAMES. Positions are render space (`positionWorld`, the light's `matrixWorld` — both carry `scene.position`); every
 * per-light datum is stored as an OFFSET from the light's own world position, so an origin rebase moves nothing.
 * Inside the shadowed light volume the march point arrives as `builder.context.positionWorld` (the volume's own
 * contract) — the mask is evaluated there and the occluder term is skipped (a volume has no receiver normal).
 */
import * as THREE from 'three/webgpu';
import { Fn, float, vec3, uniform, uniformArray, positionWorld, normalWorld, lightPosition, smoothstep, max, abs, sqrt, dot, cross, clamp, select, If, Loop, renderGroup, distance } from 'three/tsl';
import { PANE_MAX_OCCLUDERS, PANE_PERSON_R, PANE_ROOM_FADE_M, PANE_SAMPLES, PANE_SHADOW_RANGE_M } from './pane_visibility.js';
import { lookAtLocal } from './local_origin.js';

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- TSL graphs are loosely typed
type N = any;

/** A pooled window light: a RectAreaLight plus the uniforms its node reads (see the file header). */
export class WindowAreaLight extends THREE.RectAreaLight {
  readonly isWindowAreaLight = true;
  /** 0 = no mask (an exterior pane lights its side of the street); 1 = confined to the room box. */
  maskMode = 0;
  /** The room box's centre as an offset from the light's world position (world axes). */
  readonly maskOffset = new THREE.Vector3();
  /** The room box's half extents (x, y, z) in the building's frame. */
  readonly maskHalf = new THREE.Vector3(1, 1, 1);
  /** cos / sin of the building's yaw (three's rotation.y). */
  readonly maskYaw = new THREE.Vector2(1, 0);
  /** People as vertical capsules: (dx, y0, dz, y1) offsets from the light's world position. */
  readonly occluders: THREE.Vector4[] = Array.from({ length: PANE_MAX_OCCLUDERS }, () => new THREE.Vector4());
  occluderCount = 0;
  /** Build the occluder term into the shader (fixed for the session — the node reads it once). */
  visibility: boolean;
  constructor(color: THREE.ColorRepresentation, intensity: number, width: number, height: number, visibility: boolean) {
    super(color, intensity, width, height);
    this.visibility = visibility;
  }
}

const RectBase = (THREE as unknown as { RectAreaLightNode: new (light: THREE.RectAreaLight) => { setupDirectRectArea(builder: unknown): { lightColor: N } | undefined } }).RectAreaLightNode;

const _m = new THREE.Matrix4();

/** The shader side of `WindowAreaLight`. */
export class WindowRectLightNode extends RectBase {
  static get type(): string { return 'WindowRectLightNode'; }
  private readonly wl: WindowAreaLight;
  private readonly uMode: N; private readonly uOff: N; private readonly uHalf: N; private readonly uYaw: N;
  private readonly uCount: N; private readonly uOcc: N;
  private readonly uHalfW: N; private readonly uHalfH: N; private readonly uN: N;

  constructor(light: WindowAreaLight) {
    super(light);
    this.wl = light;
    const L = light;
    this.uMode = uniform(0).setGroup(renderGroup).onRenderUpdate(() => L.maskMode);
    this.uOff = uniform(L.maskOffset).setGroup(renderGroup);
    this.uHalf = uniform(L.maskHalf).setGroup(renderGroup);
    this.uYaw = uniform(L.maskYaw).setGroup(renderGroup);
    this.uCount = uniform(0, 'int').setGroup(renderGroup).onRenderUpdate(() => Math.min(PANE_MAX_OCCLUDERS, Math.max(0, L.occluderCount | 0)));
    this.uOcc = uniformArray(L.occluders, 'vec4').setGroup(renderGroup);
    // The rectangle in WORLD axes (three's node carries it in view space; the occluders live in world space).
    const hwV = new THREE.Vector3(), hhV = new THREE.Vector3(), nV = new THREE.Vector3(0, 0, -1);
    this.uHalfW = uniform(hwV).setGroup(renderGroup).onRenderUpdate(() => { _m.extractRotation(L.matrixWorld); hwV.set(L.width * 0.5, 0, 0).applyMatrix4(_m); });
    this.uHalfH = uniform(hhV).setGroup(renderGroup).onRenderUpdate(() => { _m.extractRotation(L.matrixWorld); hhV.set(0, L.height * 0.5, 0).applyMatrix4(_m); });
    // The emitting side: the light's −Z (three lights look down −Z; the module aims it along the pane's facing).
    this.uN = uniform(nV).setGroup(renderGroup).onRenderUpdate(() => { _m.extractRotation(L.matrixWorld); nV.set(0, 0, -1).applyMatrix4(_m).normalize(); });
  }

  setupDirectRectArea(builder: { context: { positionWorld?: N }; material?: { isVolumeNodeMaterial?: boolean } }): { lightColor: N } | undefined {
    const data = super.setupDirectRectArea(builder);
    if (!data) return data;
    const lp: N = lightPosition(this.wl as unknown as THREE.Light);
    const inVolume = builder.material?.isVolumeNodeMaterial === true;
    const P0: N = builder.context.positionWorld ?? positionWorld;
    const { uMode, uOff, uHalf, uYaw, uCount, uOcc, uHalfW, uHalfH, uN } = this;
    const withVis = this.wl.visibility && !inVolume;
    const factor: N = Fn(() => {
      const P: N = vec3(P0).toVar();
      // ── THE ROOM LAW: 1 inside the building's box, 0 outside (smooth over the margin) ──
      const d: N = P.sub(lp.add(uOff));
      const lx: N = uYaw.x.mul(d.x).sub(uYaw.y.mul(d.z)), lz: N = uYaw.y.mul(d.x).add(uYaw.x.mul(d.z));
      const fade = float(PANE_ROOM_FADE_M);
      const inAxis = (v: N, h: N): N => float(1).sub(smoothstep(h, h.add(fade), abs(v))); // 1 up to the face, 0 past the wall
      const room: N = inAxis(lx, uHalf.x).mul(inAxis(d.y, uHalf.y)).mul(inAxis(lz, uHalf.z));
      const k: N = select(uMode.greaterThan(0.5), room, float(1)).toVar();
      if (withVis) {
        const Nr: N = vec3(normalWorld).toVar(); // built HERE, at the top of this Fn (THE LAZY-VAR PLACEMENT) — never inside the If
        // TWO branches: the outer one is UNIFORM (no one near the pane — every lane skips, even on a masked SIMD like
        // SwiftShader's, where one combined per-fragment condition cost the full term: the bed's timing); the inner one
        // bounds the work to receivers near the pane.
        If(uCount.greaterThan(0), () => { If(distance(P, lp).lessThan(PANE_SHADOW_RANGE_M), () => {
          const area4: N = max(cross(uHalfW, uHalfH).length(), 1e-4); // (4·|hw × hh|) / 4 samples
          const qs: N[] = PANE_SAMPLES.map(([a, b]) => lp.add(uHalfW.mul(a)).add(uHalfH.mul(b)).toVar()); // the 2 × 2 cells' centres (± ½ the half extents)
          const ws: N[] = qs.map((q) => {
            const dq: N = q.sub(P);
            const r2: N = dot(dq, dq);
            const r: N = sqrt(r2).max(1e-4);
            const cosR: N = max(dot(Nr, dq).div(r), 0), cosE: N = max(dot(uN, dq).negate().div(r), 0);
            return cosR.mul(cosE).div(max(r2, area4)).toVar();
          });
          const vs: N[] = qs.map(() => float(1).toVar());
          Loop(uCount, ({ i }: { i: N }) => {
            const cap: N = uOcc.element(i);
            const cx: N = lp.x.add(cap.x), cz: N = lp.z.add(cap.z), y0: N = lp.y.add(cap.y), y1: N = lp.y.add(cap.w);
            const phx: N = P.x.sub(cx), phz: N = P.z.sub(cz);
            // a person never shadows their own body, nor the floor under their feet
            const own: N = sqrt(phx.mul(phx).add(phz.mul(phz))).lessThan(PANE_PERSON_R + 0.25).and(P.y.greaterThan(y0.sub(0.55))).and(P.y.lessThan(y1.add(0.55)));
            qs.forEach((q, j) => {
              const dx: N = q.x.sub(P.x), dy: N = q.y.sub(P.y), dz: N = q.z.sub(P.z);
              const a: N = dx.mul(dx).add(dz.mul(dz));
              const s: N = clamp(phx.mul(dx).add(phz.mul(dz)).negate().div(max(a, 1e-9)), 0, 1);
              const hx: N = phx.add(dx.mul(s)), hz: N = phz.add(dz.mul(s)), y: N = P.y.add(dy.mul(s));
              const dv: N = max(max(y0.sub(y), y.sub(y1)), 0);
              const dist: N = sqrt(hx.mul(hx).add(hz.mul(hz)).add(dv.mul(dv))).sub(PANE_PERSON_R);
              const t: N = s.mul(sqrt(a.add(dy.mul(dy))));
              const w: N = t.mul(0.05).add(0.03);
              const tr: N = smoothstep(w.negate(), w, dist);
              vs[j].mulAssign(select(own, float(1), tr));
            });
          });
          const W: N = ws.reduce((acc: N, w: N) => acc.add(w), float(0));
          const V: N = ws.reduce((acc: N, w: N, j: number) => acc.add(w.mul(vs[j])), float(0));
          k.mulAssign(select(W.greaterThan(1e-12), V.div(W.max(1e-12)), float(1)));
        }); });
      }
      return k;
    })();
    data.lightColor = data.lightColor.mul(factor);
    return data;
  }
}

/**
 * Register the node for the subclass on a renderer's node library (idempotent). Returns false where the library is
 * not reachable — the caller then keeps plain RectAreaLights (the old light) rather than lights three would skip.
 */
export function registerWindowLightNode(renderer: unknown): boolean {
  const lib = (renderer as { library?: { addLight?: (n: unknown, l: unknown) => void; getLightNodeClass?: (l: unknown) => unknown } } | null)?.library;
  if (!lib?.addLight) return false;
  if (lib.getLightNodeClass?.(WindowAreaLight) === WindowRectLightNode) return true;
  lib.addLight(WindowRectLightNode, WindowAreaLight);
  return lib.getLightNodeClass?.(WindowAreaLight) === WindowRectLightNode;
}

// ─── seating helpers (the module and the bed share them) ───────────────────────────────────────────────────────────

/**
 * Seat a pooled light on a pane: `at` is the pane's centre and `n` its FACING (from `paneFacing`), both in the light's
 * parent frame (the scene's children are absolute: render − scene.position); 5 cm off the glass, emitting along `n`.
 */
export function seatWindowLight(l: THREE.RectAreaLight, at: [number, number, number], n: [number, number, number], w: number, h: number): void {
  l.position.set(at[0] + n[0] * 0.05, at[1] + n[1] * 0.05, at[2] + n[2] * 0.05);
  l.width = w; l.height = h;
  // Up must not be parallel to the facing (a skylight faces up): lookAt with a sideways up for a near-vertical normal.
  if (Math.abs(n[1]) > 0.95) l.up.set(0, 0, 1); else l.up.set(0, 1, 0);
  // THE SCENE SHIFT: lookAt aims from the light's WORLD position and this target is in the parent's frame — past the first rebase every window light faced −shift instead of out of its pane
  lookAtLocal(l, _seatT.set(l.position.x + n[0], l.position.y + n[1], l.position.z + n[2]));
  l.updateMatrixWorld(true);
}
const _seatT = new THREE.Vector3();

/** THE ROOM LAW on a seated light: `box` in the light's parent frame (see `roomBoxFor`); `null` = no mask. */
export function setWindowRoom(l: WindowAreaLight, box: { cx: number; cz: number; y0: number; h: number; hx: number; hz: number; yaw: number } | null): void {
  if (!box) { l.maskMode = 0; return; }
  l.maskMode = 1;
  l.maskOffset.set(box.cx - l.position.x, box.y0 + box.h / 2 - l.position.y, box.cz - l.position.z);
  l.maskHalf.set(box.hx, box.h / 2, box.hz);
  l.maskYaw.set(Math.cos(box.yaw), Math.sin(box.yaw));
}

/** The people near a seated light (vertical capsules in the light's parent frame); at most `PANE_MAX_OCCLUDERS`. */
export function setWindowOccluders(l: WindowAreaLight, caps: ReadonlyArray<{ x: number; z: number; y0: number; y1: number }>): void {
  const n = Math.min(PANE_MAX_OCCLUDERS, caps.length);
  for (let i = 0; i < n; i++) {
    const c = caps[i];
    l.occluders[i].set(c.x - l.position.x, c.y0 - l.position.y, c.z - l.position.z, c.y1 - l.position.y);
  }
  l.occluderCount = n;
}
